import { ChartDataset } from "chart.js";

import { postTrackingData } from "@/api/endpoints/track";
import Line from "@/components/Charts/Line";
import { useI18n } from "@/contexts/i18nContext";
import { EVENT } from "@/tracking_events";
import ClickHandlerPlugin from "@components/Charts/plugins/ClickHandlerPlugin";
import IndexPlugin from "@components/Charts/plugins/IndexPlugin";
import { dynamicIntervalLabels } from "@components/Charts/utils/dynamicIntervalLabels";
import prepareDatasets from "@components/Charts/utils/prepareDataset";

import { BaseWidget } from "../BaseWidget";
import {
  PackedWidgetData,
  tooltipWithDateRangeCallback,
  unpackWidgetData,
} from "./utils";
import { BaseWidgetProps, WidgetProps } from "./WidgetProps";
type ChartDataType = ChartDataset<"line", number[]>;
type ReachData = PackedWidgetData<ChartDataType>;
type ReachWidgetProps = WidgetProps<ReachData>;
export type ExternalReachWidgetProps = BaseWidgetProps & ReachWidgetProps;

const Reach: React.FunctionComponent<ExternalReachWidgetProps> = (props) => {
  const { data, filters } = props;
  const chartData = prepareDatasets(unpackWidgetData<ChartDataType>(data));

  const { formatDate } = useI18n();

  const hasData = data?.some(({ results }) =>
    results?.dataset?.some(({ data }) => data?.some((d: number) => d > 0))
  );

  return (
    <BaseWidget
      // The types here should be fixed within BaseWidget. That is out of scope for now.
      hasData={hasData}
      {...props}
    >
      {data?.length === 1 && (
        <Line
          options={{
            plugins: {
              click: {
                onClick: (appliedFilters) => {
                  postTrackingData(EVENT.WIDGET_CLICK, {
                    widget: props.metric,
                  });
                  props.fetchMentions_v2(appliedFilters);
                },
              },
              legend: {
                display: !props.hideLegend,
              },
              tooltip: {
                callbacks: {
                  title: tooltipWithDateRangeCallback(
                    chartData.labels,
                    data?.[0]?.meta?.time_period,
                    formatDate,
                    filters?.end_date
                  ),
                },
              },
            },
            scales: {
              y: {
                ticks: {
                  precision: 0, // forces integers
                },
                min: 0,
              },
              x: {
                type: "time",
                ticks: {
                  display: false,
                  source: "labels",
                },
                grid: {
                  drawTicks: true,
                },
                time: {
                  minUnit: "month",
                  tooltipFormat: "PP",
                },
              },
              x2: dynamicIntervalLabels(chartData.meta?.time_period),
            },
          }}
          data={chartData}
          plugins={[ClickHandlerPlugin, IndexPlugin]}
        />
      )}
    </BaseWidget>
  );
};

export default Reach;
